import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AuthService } from '@core/http/auth.service';
import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from "@angular/common/http";
import { HttpMesageHandlerService } from "@core/service/http-mesage-handler";
import { REGEX } from "@shared/utils/regex.constants";
import { NgIf } from "@angular/common";

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    RouterLink,
    MatButtonModule,
    NgIf,
  ],
})
export class ResetPasswordComponent implements OnInit {
  authForm!: UntypedFormGroup;
  hide = true;
  chide = true;

  token: string = '';
  passwordFormat = Validators.pattern(REGEX.password);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private httpMsgHandler: HttpMesageHandlerService
  ) {
    this.route.params.subscribe((param) => {
      this.token = (param['token'] ?? '').toString().trim();   // <-- SEGURO
    });
  }

  get password() {
    return this.authForm.get('password');
  }

  get confirmedPassword() {
    return this.authForm.get('confirmedPassword');
  }

  get passwordsDoNotMatch() {
    return (
      this.authForm.errors?.['passwordsMismatch'] &&
      (this.password?.touched || this.confirmedPassword?.touched)
    );
  }

  ngOnInit() {
    this.authForm = this.formBuilder.group(
      {
        password: ['', [Validators.required, this.passwordFormat]],
        confirmedPassword: ['', [Validators.required, this.passwordFormat]],
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  resetPassword() {
    if (this.authForm.invalid || this.passwordsDoNotMatch) {
      this.authForm.markAllAsTouched();
      return;
    }

    const password = this.password?.value?.trim() || '';
    const confirmedPassword = this.confirmedPassword?.value?.trim() || '';

    if (!password || !confirmedPassword || password !== confirmedPassword) {
      return;
    }

    this.authService
      .resetPassword(this.token, password, confirmedPassword)
      .subscribe({
        next: (res: DefaultResponse<string>) => {
          const safeCode =
            res.code && String(res.code).trim() !== ''
              ? String(res.code)
              : 'CONTRASEÑA ACTUALIZADA';

          Swal.fire({
            icon: 'success',
            title: safeCode,
            html: res.message ?? 'Tu contraseña ha sido actualizada correctamente.',
          }).then(() => {
            this.router.navigate(['/authentication/signin']);
          });
        },
        error: (err: HttpErrorResponse) => {
          this.httpMsgHandler.handleMsg(err);
        },
      });
  }

  passwordsMatchValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const pw = group.get('password')?.value;
    const cpw = group.get('confirmedPassword')?.value;
    return pw === cpw ? null : { passwordsMismatch: true };
  };
}
